import React, { useState, useEffect } from 'react';
import BaseCard from '../../common/layout/BaseCard';
import { Button } from '../../common/ui/Button';
import { PlusIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../../lib/supabase';
import { captureAppException } from '../../../utils/errorCaptureUtils';
import { captureCustomEvent } from '../../../utils/posthogUtils';
import { getPublicUrl } from '../../../utils/filestorageUtils';
import Pill from '../../common/display/Pill';
import { Dropdown } from '../../common/ui/Listbox';
import { Input } from '../../common/ui/Input';
import { DatePicker } from '../../common/ui/DatePicker';
import { EventMarkdownEditor } from '../../common/editor/EventMarkdownEditor';
import { Modal } from '../../common/ui/Modal';
import { CommunityUpdate } from '../types';
import { ImageUpload, ImageUploadRef } from '../../common/ui/ImageUpload';

interface ManageCommunityUpdatesProps {
  communityId: string;
}

const VISIBILITY_PILL_CLASSES: Record<string, string> = {
  public: 'bg-green-100 text-green-700',
  draft: 'bg-yellow-100 text-yellow-800',
  restricted: 'bg-gray-100 text-gray-600',
};

const VISIBILITY_OPTIONS = [
  { id: 'draft', text: 'Draft', disabled: false },
  { id: 'restricted', text: 'Members Only', disabled: false },
  { id: 'public', text: 'Public', disabled: false },
];

interface EditorProps {
  communityId: string;
  onSave: () => void;
  onCancel: () => void;
  initialData?: CommunityUpdate;
  isInline?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  updateId?: string;
}

const NewCommunityUpdateEditor: React.FC<EditorProps> = ({ communityId, onSave, onCancel, initialData, isInline, onDirtyChange, updateId = initialData?.id || 'new' }) => {
  const [title, setTitle] = React.useState(initialData?.title || '');
  const [visibility, setVisibility] = React.useState<'public' | 'restricted' | 'draft'>(initialData?.visibility || 'public');
  const [content, setContent] = React.useState(initialData?.content || '');
  const [image, setImage] = React.useState<string>(initialData?.image || '');
  const [createdAt, setCreatedAt] = React.useState<Date | null>(initialData ? new Date(initialData.created_at) : new Date());
  const [createdTime, setCreatedTime] = React.useState<string>(() => {
    const date = initialData ? new Date(initialData.created_at) : new Date();
    return date.toTimeString().slice(0, 5);
  });
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const imageUploadRef = React.useRef<ImageUploadRef>(null);
  const [isContentOverLimit, setIsContentOverLimit] = React.useState(false);

  const getImageUrl = React.useCallback((path: string | null): string => {
    if (!path || path === 'pending') return '';
    return getPublicUrl({ bucket: 'community_images', path }) || '';
  }, []);

  const imageUrl = React.useMemo(() => getImageUrl(image), [getImageUrl, image]);

  // Track dirty state
  useEffect(() => {
    const changed =
      title !== (initialData?.title || '') ||
      visibility !== (initialData?.visibility || 'public') ||
      content !== (initialData?.content || '') ||
      image !== (initialData?.image || '') ||
      (createdAt && initialData && new Date(initialData.created_at).toISOString().slice(0, 10) !== createdAt.toISOString().slice(0, 10)) ||
      createdTime !== (initialData ? new Date(initialData.created_at).toTimeString().slice(0, 5) : new Date().toTimeString().slice(0, 5));
    onDirtyChange?.(changed);
  }, [title, visibility, content, image, createdAt, createdTime]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    
    captureCustomEvent('community_update_save_initiated', {
      eventProperties: {
        community_id: communityId,
        update_id: initialData?.id || 'new',
        visibility: visibility,
        has_image: !!image,
        is_edit: !!initialData
      }
    });
    
    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error('Not authenticated');
      
      const finalDate = createdAt ? new Date(createdAt) : new Date();
      if (createdTime) {
        const [hours, minutes] = createdTime.split(':').map(Number);
        finalDate.setHours(hours, minutes, 0, 0);
      }

      if (!initialData) {
        // New post flow
        const { data, error: insertError } = await supabase
          .from('community_updates')
          .insert([{
            community_id: communityId,
            title,
            visibility,
            content,
            created_at: finalDate.toISOString(),
            author_id: user.id,
          }] as const)
          .select('id')
          .single();
        
        if (insertError) throw insertError;
        if (!data) throw new Error('Failed to create post: No data returned');
        
        if (image === 'pending') {
          await imageUploadRef.current?.uploadPendingFile(data.id, finalDate.toISOString());
        }
        
        captureCustomEvent('community_update_created', {
          eventProperties: {
            community_id: communityId,
            update_id: data.id,
            visibility: visibility,
            has_image: !!image
          }
        });
      } else {
        // Edit flow
        const updateData = {
          community_id: communityId,
          title,
          visibility,
          content,
          created_at: finalDate.toISOString(),
          author_id: initialData.author_id,
        } as const;

        // Only include image in update if it's not pending
        if (image !== 'pending') {
          (updateData as any).image = image;
        }

        const { error: updateError } = await supabase
          .from('community_updates')
          .update(updateData)
          .eq('id', initialData.id);
        
        if (updateError) throw updateError;

        // Handle pending image upload for existing post
        if (image === 'pending') {
          await imageUploadRef.current?.uploadPendingFile(initialData.id, finalDate.toISOString());
        }
        
        captureCustomEvent('community_update_updated', {
          eventProperties: {
            community_id: communityId,
            update_id: initialData.id,
            visibility: visibility,
            has_image: !!image
          }
        });
      }

      onSave();
    } catch (error) {
      captureAppException(error, {
        tags: {
          context: 'manage_community_updates',
          action: 'save_update',
          community_id: communityId,
          update_id: initialData?.id || 'new',
          is_edit: !!initialData
        }
      });

      setError('Failed to save update. Please try again later.');
    } finally {
      setSaving(false);
    }
  };

  const handleImageChange = async (path: string | null) => {
    setImage(path === null ? '' : path);
  };

  return (
    <div className={`${isInline ? 'py-2' : 'mb-8 p-6 bg-gray-50 rounded-lg border border-gray-200'}`}>
      <div className="flex flex-col md:flex-row gap-6">
        {/* Left column - Image Upload */}
        <div className="md:w-1/4">
          <ImageUpload
            ref={imageUploadRef}
            type="post"
            currentImageUrl={imageUrl}
            onImageChange={handleImageChange}
            maxSizeMB={15}
            bucketName="community_images"
            filePath={`${communityId}/updates/${updateId}/image_${new Date().toISOString()}.png`}
            tableName="community_updates"
            columnName="image"
            recordId={updateId}
            label="Post Image"
            disabled={saving}
            onDelete={() => setImage('')}
            helperText="Upload a square image for your post. Recommended size: 1080x1080 pixels."
            delayUpload={!initialData}
          />
        </div>

        {/* Right column - Form Fields */}
        <div className="md:w-3/4 space-y-4">
          <Input
            label="Title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            className="bg-white"
            maxLength={120}
            isRequired
          />

          <Dropdown
            label="Visibility"
            options={VISIBILITY_OPTIONS}
            value={visibility}
            onChange={opt => setVisibility(opt.id as 'public' | 'restricted' | 'draft')}
            className="bg-white"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DatePicker
              label="Created At"
              value={createdAt}
              onChange={date => setCreatedAt(date as Date)}
              className="bg-white"
            />
            <div className="flex flex-col">
              <label className="text-sm text-gray-700 mb-1 ml-1">Time</label>
              <input
                type="time"
                value={createdTime}
                onChange={e => setCreatedTime(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 bg-white h-10 text-base"
                aria-label="Time"
              />
            </div>
          </div>

          <Input
            label="Author"
            value=""
            disabled
            placeholder="Author selection functionality is WIP"
            className="bg-gray-50 text-gray-500"
          />
        </div>
      </div>

      <div className="mt-6">
        <EventMarkdownEditor
          content={content}
          onChange={setContent}
          eventId={communityId}
          supabase={supabase}
          minHeight="250px"
          placeholder="Write your update in markdown..."
          disabledPlugins={['image']}
          maxLength={2500}
          onLengthChange={setIsContentOverLimit}
        />
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md mt-4">{error}</div>}
      <div className="flex gap-2 justify-end mt-4">
        <Button onClick={onCancel} variant="ghost" size="small">Cancel</Button>
        <Button 
          onClick={handleSave} 
          color="primary" 
          size="small" 
          loading={saving} 
          disabled={saving || !title || !content || isContentOverLimit}
        >
          {initialData ? 'Save Changes' : 'Create Update'}
        </Button>
      </div>
    </div>
  );
};

export const ManageCommunityUpdates: React.FC<ManageCommunityUpdatesProps> = ({ communityId }) => {
  const [updates, setUpdates] = useState<CommunityUpdate[]>([]);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [pendingEditId, setPendingEditId] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [editorDirty, setEditorDirty] = useState(false);

  useEffect(() => {
    const fetchUpdates = async () => {
      setLoading("fetching");
      setError(null);
      try {
        const { data, error } = await supabase
          .from('community_updates')
          .select(`
            id, title, author_id, visibility, is_pinned, content, created_at, updated_at, image,
            author:author_id ( first_name, last_name, email )
          `)
          .eq('community_id', communityId)
          .order('is_pinned', { ascending: false })
          .order('created_at', { ascending: false });

        if (error) throw error;
        setUpdates(
          data.map((update: any) => ({
            ...update,
            author: Array.isArray(update.author) ? update.author[0] : update.author
          }))
        );
      } catch (error) {
        captureAppException(error, {
          tags: {
            context: 'manage_community_updates',
            action: 'fetch_updates',
            community_id: communityId
          }
        });
        setUpdates([]);
        setError('Failed to fetch community updates.');
      } finally {
        setLoading(null);
      }
    };
    fetchUpdates();
  }, [communityId, showEditor]);

  const handleTogglePin = async (updateId: string, currentPinnedStatus: boolean) => {
    setLoading(`pinning:${updateId}`); // Use a specific loading state for pinning
    try {
      const { error } = await supabase
        .from('community_updates')
        .update({ is_pinned: !currentPinnedStatus })
        .eq('id', updateId);
      if (error) throw error;
      // Refetch updates to reflect the new order and status
      const { data, error: fetchError } = await supabase
        .from('community_updates')
        .select('id, title, author_id, visibility, is_pinned, content, created_at, updated_at, author:author_id ( first_name, last_name, email )')
        .eq('community_id', communityId)
        .order('is_pinned', { ascending: false })
        .order('created_at', { ascending: false });
      if (fetchError) throw fetchError;
      setUpdates(
        data.map((update: any) => ({
          ...update,
          author: Array.isArray(update.author) ? update.author[0] : update.author
        }))
      );
    } catch (err) {
      captureAppException(err, { tags: { context: 'manage_community_updates', community_id: communityId, update_id: updateId, action: 'toggle_pin' } });
      setError('Failed to update pin status.');
    } finally {
      setLoading(null);
    }
  };

  // Handler for opening an editor (new or edit)
  const handleOpenEditor = (id: string | null) => {
    if (editorDirty) {
      setPendingEditId(id);
      setShowConfirm(true);
    } else {
      setEditingId(id);
      setShowEditor(id === null);
    }
  };

  // Handler for confirming discard
  const handleConfirmDiscard = () => {
    setShowConfirm(false);
    setEditingId(pendingEditId);
    setShowEditor(pendingEditId === null);
    setPendingEditId(null);
    setEditorDirty(false);
  };

  // Handler for canceling discard
  const handleCancelDiscard = () => {
    setShowConfirm(false);
    setPendingEditId(null);
  };

  return (
    <BaseCard>
      <Modal isOpen={showConfirm} onClose={handleCancelDiscard}>
        <div className="p-4">
          <h3 className="text-lg font-bold mb-2">Discard changes?</h3>
          <p className="mb-4">You have unsaved changes. Are you sure you want to discard them?</p>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={handleCancelDiscard}>Cancel</Button>
            <Button color="danger" onClick={handleConfirmDiscard}>Discard</Button>
          </div>
        </div>
      </Modal>
      <p className="text-sm tmb-6 rounded-md p-2 pb-4">
        Manage your community updates here. Create new entries, edit existing ones, or pin important announcements to the top.
        Updates are displayed on the main community page in reverse chronological order (latest first), with pinned updates always appearing at the top.
      </p>
      <div className="flex items-center justify-between mb-4">
        <Button variant="outline" size="small" onClick={() => handleOpenEditor(null)} className="mb-4">
          <PlusIcon className="h-4 w-4 mr-2" /> New Update
        </Button>
      </div>
      {showEditor && editingId === null && (
        <NewCommunityUpdateEditor
          communityId={communityId}
          onSave={() => { setShowEditor(false); setEditorDirty(false); }}
          onCancel={() => {
            if (editorDirty) setShowConfirm(true);
            else setShowEditor(false);
          }}
          onDirtyChange={setEditorDirty}
        />
      )}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md mb-4">{error}</div>
      )}
      {loading ? (
        <div className="text-center text-gray-500 py-8">Loading...</div>
      ) : updates.length === 0 ? (
        <div className="text-center text-gray-500 py-8">No updates yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-600 border-b">
                <th className="py-2 px-3 w-12">Pin</th>
                <th className="py-2 px-3">Reference</th>
                <th className="py-2 px-3">Author</th>
                <th className="py-2 px-3">Visibility</th>
                <th className="py-2 px-3">Created</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {updates.map(update => (
                editingId === update.id ? (
                  <tr key={update.id}>
                    <td colSpan={6} className="p-0">
                      <BaseCard className="my-4 p-6 bg-slate-50 border border-slate-200">
                        <NewCommunityUpdateEditor
                          communityId={communityId}
                          initialData={update}
                          isInline={true}
                          onSave={() => { setEditingId(null); setEditorDirty(false); }}
                          onCancel={() => {
                            if (editorDirty) setShowConfirm(true);
                            else setEditingId(null);
                          }}
                          onDirtyChange={setEditorDirty}
                          updateId={update.id}
                        />
                      </BaseCard>
                    </td>
                  </tr>
                ) : (
                  <tr key={update.id} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-3">
                      <input
                        type="checkbox"
                        checked={!!update.is_pinned}
                        onChange={() => handleTogglePin(update.id, !!update.is_pinned)}
                        disabled={loading === `pinning:${update.id}`}
                        className="form-checkbox h-5 w-5 text-primary-base rounded focus:ring-primary-base/50 border-gray-300 cursor-pointer disabled:opacity-50"
                      />
                    </td>
                    <td className="py-2 px-3">{update.title}</td>
                    <td className="py-2 px-3">{
                      (update.author?.first_name || update.author?.last_name)
                        ? [update.author?.first_name, update.author?.last_name].filter(Boolean).join(' ')
                        : (update.author?.email || update.author_id)
                    }</td>
                    <td className="py-2 px-3 capitalize">{
                      <Pill
                        text={update.visibility === 'restricted' ? 'Members Only' : update.visibility.charAt(0).toUpperCase() + update.visibility.slice(1)}
                        className={VISIBILITY_PILL_CLASSES[update.visibility] || 'bg-gray-100 text-gray-600'}
                      />
                    }</td>
                    <td className="py-2 px-3">{new Date(update.created_at).toLocaleString()}</td>
                    <td className="py-2 px-3">
                      <Button 
                        variant="outline" 
                        size="small"
                        onClick={() => handleOpenEditor(update.id)}
                      >
                        Edit Entry
                      </Button>
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
      )}
    </BaseCard>
  );
}; 